import { FormEvent, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Navigate, useLocation } from "react-router-dom";
import { ShiftLogo } from "@/components/layout/ShiftLogo";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Input";
import { useAuth } from "@/contexts/AuthContext";
import { getErrorMessage } from "@/lib/utils";

const LOGIN_VIDEO_URL =
  "https://qlxnmbgtohaiyhbzfvvc.supabase.co/storage/v1/object/public/car%20images/Video/compressed-EstXs9LX.mp4";

const LOGIN_FALLBACK_IMAGE_URL =
  "https://qlxnmbgtohaiyhbzfvvc.supabase.co/storage/v1/object/public/car%20images/Ferrari/ferrari-new-luce-concept.png";

const DESKTOP_LOGIN_MIN_WIDTH = 1200;
const LOGIN_REVEAL_AT_SECONDS = 4;
const VIDEO_LOAD_TIMEOUT_MS = 8000;

/** Apple-style deceleration — slow, smooth settle */
const premiumEase = [0.16, 1, 0.3, 1] as const;

const logoIntroHidden = {
  opacity: 0,
  scale: 0.86,
  y: 24,
  filter: "blur(14px)",
} as const;

const logoIntroVisible = {
  opacity: 1,
  scale: 1,
  y: 0,
  filter: "blur(0px)",
} as const;

const loginRevealHidden = {
  opacity: 0,
  x: 72,
  y: 16,
  scale: 0.96,
  filter: "blur(10px)",
} as const;

const loginRevealVisible = {
  opacity: 1,
  x: 0,
  y: 0,
  scale: 1,
  filter: "blur(0px)",
} as const;

function useDesktopLoginLayout() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(min-width: ${DESKTOP_LOGIN_MIN_WIDTH}px)`).matches
      : false
  );

  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${DESKTOP_LOGIN_MIN_WIDTH}px)`);
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isDesktop;
}

function LoginCard({
  email,
  password,
  error,
  submitting,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onDarkBackground = false,
}: {
  email: string;
  password: string;
  error: string | null;
  submitting: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onDarkBackground?: boolean;
}) {
  return (
    <div className="w-full max-w-md">
      <div className="panel p-8 sm:p-10">
        <div className="mb-8 flex flex-col items-center text-center">
          <ShiftLogo height={32} />
          <p className="mt-6 text-sm text-muted">
            Sign in with your admin account to access the control center.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <Field label="Email">
            <Input
              type="email"
              name="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              required
            />
          </Field>

          <Field label="Password">
            <Input
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              required
            />
          </Field>

          {error ? (
            <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="brand" size="lg" className="w-full" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>

      <p
        className={
          onDarkBackground
            ? "mt-6 text-center text-xs text-white/90"
            : "mt-6 text-center text-xs text-muted-light"
        }
      >
        Admin access only. Contact your Shift administrator if you need access.
      </p>
    </div>
  );
}

function MobileLoginLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 min-[1200px]:hidden">
      {children}
    </div>
  );
}

function LogoIntroSplash() {
  return (
    <motion.div
      className="absolute inset-0 z-20 flex items-center justify-center bg-white"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.9, ease: premiumEase }}
    >
      <motion.div
        initial={logoIntroHidden}
        animate={logoIntroVisible}
        exit={{ opacity: 0, scale: 1.05, filter: "blur(6px)" }}
        transition={{
          duration: 1.5,
          ease: premiumEase,
        }}
      >
        <ShiftLogo height={56} />
      </motion.div>
    </motion.div>
  );
}

function DesktopLoginLayout({
  children,
  showLogin,
  onRevealLogin,
}: {
  children: ReactNode;
  showLogin: boolean;
  onRevealLogin: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasRevealedRef = useRef(false);
  const mediaActiveAtRef = useRef<number | null>(null);
  const reduceMotion = useReducedMotion();
  const [showLogoSplash, setShowLogoSplash] = useState(!reduceMotion);
  const [backgroundMode, setBackgroundMode] = useState<"loading" | "video" | "image">(
    reduceMotion ? "image" : "loading"
  );

  const revealLogin = useCallback(() => {
    if (hasRevealedRef.current) return;
    hasRevealedRef.current = true;
    onRevealLogin();
  }, [onRevealLogin]);

  const activateBackground = useCallback((mode: "video" | "image") => {
    setBackgroundMode(mode);
    setShowLogoSplash(false);
    if (mediaActiveAtRef.current === null) {
      mediaActiveAtRef.current = Date.now();
    }
  }, []);

  // Preload fallback image
  useEffect(() => {
    const img = new Image();
    img.src = LOGIN_FALLBACK_IMAGE_URL;
  }, []);

  // Load video — dismiss logo when playback starts; fall back to image on failure
  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) {
      activateBackground("image");
      return;
    }

    let settled = false;

    const useFallback = () => {
      if (settled) return;
      settled = true;
      video.pause();
      activateBackground("image");
    };

    const onPlaying = () => {
      if (settled) return;
      settled = true;
      activateBackground("video");
    };

    const tryPlay = async () => {
      try {
        await video.play();
      } catch {
        useFallback();
      }
    };

    const onCanPlay = () => {
      void tryPlay();
    };

    const loadTimeout = window.setTimeout(useFallback, VIDEO_LOAD_TIMEOUT_MS);

    video.addEventListener("playing", onPlaying);
    video.addEventListener("error", useFallback);
    video.addEventListener("canplaythrough", onCanPlay);

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      void tryPlay();
    }

    return () => {
      settled = true;
      window.clearTimeout(loadTimeout);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("error", useFallback);
      video.removeEventListener("canplaythrough", onCanPlay);
    };
  }, [activateBackground, reduceMotion]);

  // Reveal login 4s after video/image background becomes active
  useEffect(() => {
    if (backgroundMode === "loading" || reduceMotion) return;

    const video = videoRef.current;
    const startedAt = mediaActiveAtRef.current ?? Date.now();
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, LOGIN_REVEAL_AT_SECONDS * 1000 - elapsed);

    const revealTimer = window.setTimeout(revealLogin, remaining);

    const onTimeUpdate = () => {
      if (video && video.currentTime >= LOGIN_REVEAL_AT_SECONDS) {
        revealLogin();
      }
    };

    if (backgroundMode === "video" && video) {
      video.addEventListener("timeupdate", onTimeUpdate);
    }

    return () => {
      window.clearTimeout(revealTimer);
      video?.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [backgroundMode, revealLogin, reduceMotion]);

  function handleVideoEnded() {
    const video = videoRef.current;
    if (video) {
      video.pause();
      if (Number.isFinite(video.duration) && video.duration > 0) {
        video.currentTime = Math.max(0, video.duration - 0.04);
      }
    }
    revealLogin();
  }

  return (
    <div className="relative hidden min-h-screen overflow-hidden bg-black min-[1200px]:block">
      <img
        src={LOGIN_FALLBACK_IMAGE_URL}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000"
        style={{ opacity: backgroundMode === "image" ? 1 : 0 }}
      />

      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000"
        style={{ opacity: backgroundMode === "video" ? 1 : 0 }}
        src={LOGIN_VIDEO_URL}
        muted
        playsInline
        preload="auto"
        aria-hidden
        onEnded={handleVideoEnded}
      />

      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/35 via-black/15 to-black/45 transition-opacity duration-1000"
        style={{ opacity: showLogin ? 1 : 0 }}
        aria-hidden
      />

      <AnimatePresence>{showLogoSplash ? <LogoIntroSplash key="logo-intro" /> : null}</AnimatePresence>

      <div className="relative z-10 flex min-h-screen items-center justify-end px-16 py-12">
        <motion.div
          className="w-full max-w-md will-change-transform"
          initial={loginRevealHidden}
          animate={showLogin ? loginRevealVisible : loginRevealHidden}
          transition={
            reduceMotion
              ? { duration: 0 }
              : {
                  duration: 1.65,
                  ease: premiumEase,
                }
          }
          style={{ pointerEvents: showLogin ? "auto" : "none" }}
          aria-hidden={!showLogin}
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { signIn, session, isAdmin, loading } = useAuth();
  const location = useLocation();
  const isDesktopLayout = useDesktopLoginLayout();
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showLogin, setShowLogin] = useState(Boolean(reduceMotion));

  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";

  const handleRevealLogin = useCallback(() => {
    setShowLogin(true);
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const message = await signIn(email, password);
      if (message) setError(message);
    } catch (err) {
      setError(getErrorMessage(err, "Sign in failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  if (!loading && session && isAdmin) {
    return <Navigate to={from} replace />;
  }

  const loginCard = (
    <LoginCard
      email={email}
      password={password}
      error={error}
      submitting={submitting}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onSubmit={handleSubmit}
      onDarkBackground={isDesktopLayout}
    />
  );

  if (isDesktopLayout) {
    return (
      <DesktopLoginLayout showLogin={showLogin} onRevealLogin={handleRevealLogin}>
        {loginCard}
      </DesktopLoginLayout>
    );
  }

  return <MobileLoginLayout>{loginCard}</MobileLoginLayout>;
}
